import { useToast } from '@melio/penny';
import { FeatureFlags, useFeature } from '@melio/platform-feature-flags';
import { useMelioIntl } from '@melio/platform-i18n';
import { useLocation } from '@melio/platform-utils';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { DEFAULT_DASHBOARD_ROUTE } from '../../constants';
import { usePlanInfo, useSubscriptionPlanTier } from '../../utils';
import { SubscriptionCancelModalActivity } from '../SubscriptionCancelModal';
import { SubscriptionCancelSuccessModalActivity } from '../SubscriptionCancelSuccessModal';
import { SubscriptionFeedbackModalActivity } from '../SubscriptionFeedbackModal';

type SubscriptionCancelOrDowngradeProps = {
  isOpen: boolean;
  newPlanId?: string;
  onClose: () => void;
};

export const SubscriptionCancelOrDowngradeActivity = ({
  isOpen,
  newPlanId,
  onClose,
}: SubscriptionCancelOrDowngradeProps) => {
  const [isPlatformSubscriptionsEnabled] = useFeature(FeatureFlags.PlatformSubscriptions, false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState<boolean>(false);
  const [isSeeYouSoonModalOpen, setIsSeeYouSoonModalOpen] = useState<boolean>(false);

  const { state } = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { formatMessage } = useMelioIntl();
  const { freePlan } = useSubscriptionPlanTier();
  const { planName: newPlanName } = usePlanInfo({ planId: newPlanId });

  const shouldOpenFeedbackModal = freePlan?.id === newPlanId;
  const { returnUrl } = (state as { returnUrl?: string }) || {};

  const redirectToDashboardWithToast = () => {
    navigate(returnUrl || DEFAULT_DASHBOARD_ROUTE);
    toast({
      type: 'success',
      title: formatMessage('activities.subscription.cancelOrDowngrade.toast.success', { planName: newPlanName }),
    });
  };

  const cancelModal = {
    isOpen: isOpen && isPlatformSubscriptionsEnabled,
    onSuccess: () => {
      if (shouldOpenFeedbackModal) {
        setIsFeedbackModalOpen(true);
      } else {
        redirectToDashboardWithToast();
      }
      onClose();
    },
    onClose,
  };

  const feedbackModal = {
    isOpen: isFeedbackModalOpen && isPlatformSubscriptionsEnabled,
    onSuccess: () => {
      setIsSeeYouSoonModalOpen(true);
      setIsFeedbackModalOpen(false);
    },
    onClose: () => {
      setIsSeeYouSoonModalOpen(true);
      setIsFeedbackModalOpen(false);
    },
  };

  const seeYouSoonModal = {
    isOpen: isSeeYouSoonModalOpen && isPlatformSubscriptionsEnabled,
    onClose: () => {
      redirectToDashboardWithToast();
      setIsSeeYouSoonModalOpen(false);
    },
  };

  return (
    <>
      <SubscriptionCancelModalActivity
        isOpen={cancelModal.isOpen}
        onClose={cancelModal.onClose}
        onSuccess={cancelModal.onSuccess}
      />
      <SubscriptionFeedbackModalActivity
        isOpen={feedbackModal.isOpen}
        onClose={feedbackModal.onClose}
        onSuccess={feedbackModal.onSuccess}
      />
      <SubscriptionCancelSuccessModalActivity isOpen={seeYouSoonModal.isOpen} onClose={seeYouSoonModal.onClose} />
    </>
  );
};
